import { $selectedImage } from "@/stores/selectedImage";
import { $visualizer } from "@/stores/visualizer";
import {
  suppliedArtLabel,
  effectivePricing,
  isSuppliedArtEligible,
  prevPrintedNote,
} from "@/stores/prevPrinted";
import { $cart } from "@/stores/cart";
import {
  isMultiWindow,
  multiWindowOptions,
  multiWindowPrints,
  multiWindowSizesText,
} from "@/stores/multiWindow";
import { frameProps } from "@/utils/constants/frameProps";
import {
  customBackingOf,
  customMatOf,
  customMouldingApplies,
  customMouldingLabel,
  customMouldingOf,
  customPartLabel,
  isCustomMouldingLabel,
} from "@/utils/content/customMoulding";
import { frameMountType } from "@/utils/content/frameMount";
import {
  MOUNT_FLOATER_FACE,
  mountFloaterLabel,
  mountOnlyOf,
  mountRevealLabel,
  mountSubstrateMaterial,
  mountSubstrateName,
} from "@/utils/content/mountOnly";
import { PRODUCT_TYPE } from "@/utils/content/productTypes";
import {
  MAT_COLORS,
  BACKING_COLORS,
} from "@/utils/content/framingLabels";
import { useStore } from "@nanostores/react";
import Cookies from "js-cookie";
import { useMemo } from "react";

export const useLineItemJson = () => {
  const selectedImage = useStore($selectedImage);
  const store = useStore($visualizer);

  return useMemo(() => createLineItemJson(), [selectedImage, store]);
};

// FIX 2026-08-30: mat/backing color was dropped from all order-document
// strings on 2023-08-09 (commit c25dee9 removed the raw hexColor rather than
// mapping it to a name). Resolve the stored hex back to its catalog label so
// the work order / confirmation / packing slip state the chosen color.
// Unknown hex (e.g. the untouched default) resolves to "" and the strings
// render exactly as they did before this fix.
const matColorLabel = (matboard?: {
  label?: string;
  hex?: string;
  matBevelColor?: string;
  custom?: any;
}): string => {
  // Staff custom (special-order) mat: the colour name IS the custom label (customMoulding.ts).
  if (matboard?.custom) return customPartLabel(matboard.custom);
  if (matboard?.label) return matboard.label;
  const hex = matboard?.hex?.toLowerCase();
  if (!hex) return "";
  // hex alone is ambiguous (White vs White with Black Core share #ffffff),
  // so prefer a (hex, bevel) match, then fall back to hex alone.
  const bevel = matboard?.matBevelColor?.toLowerCase();
  const byBoth = MAT_COLORS.find(
    c =>
      c.hex.toLowerCase() === hex &&
      !!bevel &&
      c.bevelColor.toLowerCase() === bevel
  );
  const byHex = MAT_COLORS.find(c => c.hex.toLowerCase() === hex);
  return (byBoth || byHex)?.label || "";
};

// `standIn` = ignore a custom backing and name the colour shown (spacers follow the stand-in).
const backingColorLabel = (
  backing?: { hex?: string; custom?: any },
  standIn = false
): string => {
  if (backing?.custom && !standIn) return customPartLabel(backing.custom);
  const hex = backing?.hex?.toLowerCase();
  if (!hex) return "";
  return BACKING_COLORS.find(c => c.hex.toLowerCase() === hex)?.label || "";
};

const withColor = (label: string): string => (label ? `${label} ` : "");

// Materials column (Michael 09-24): a custom moulding reads like any frame — "custom decor 123 Frame (special
// order)", cf. "Black Gallery Frame". Materials text only: the line's frame label / description keep the plain
// custom label, which the cut list recognises (isCustomMouldingLabel).
const frameMaterial = (label: string): string =>
  isCustomMouldingLabel(label) ? String(label).replace(/\s*\(special order\)\s*$/i, " Frame (special order)") : label;

// The one mat bullet (Michael 09-24): '2" White 4-ply Mat'; a custom mat names the board after the mat,
// '2" 4-ply Mat, custom Crescent 1234 (special order)'; a multi-window mat leads with its windows.
const matText = (mb: any, windows?: number): string => {
  const head = windows ? `${windows}-window` : `${mb?.size}"`;
  if (mb?.custom) return `${head} ${mb?.ply} Mat, ${customPartLabel(mb.custom)}`;
  return `${head} ${withColor(matColorLabel(mb))}${mb?.ply} Mat`;
};

/**
 * strict: true only from the cart add / update (stores/cart.ts). The same builder also runs inside render
 * (useLineItemJson → summary panels), where a throw crashes the page — so the multi-window guards throw only
 * when adding / saving (staging 09-23: ticking the box before any print crashed the visualizer).
 */
export const createLineItemJson = (opts: { strict?: boolean } = {}) => {
  const selectedImage = $selectedImage.get();
  const store = $visualizer.get();

  // FIX 2026-07-23: deep-copy the frameProps template. This function used to
  // assign the SHARED constants object (`itemJson = frameProps`) and then
  // mutate it — every cart-item build overwrote the app-wide frame defaults
  // in place. That survived unnoticed while real frame data was written back;
  // the 7/22 phantom-frame fix started writing a ZEROED frameOptions for
  // fine-art items, which clobbered frameProps globally — the visualizer's
  // frame-material pipeline (frameUpdater -> getFrameMat) then read null
  // config and crashed the canvas (blank visualizer, "Cannot read properties
  // of null (reading 'current')"). Cloning also stops fields leaking between
  // successively built items of different product types.
  const itemJson: any = structuredClone(frameProps);
  // "Print previously purchased" (staff): waive the print component. The
  // stored price is what checkout charges, so the reduction happens here.
  const pricing = effectivePricing(store);
  // Multi-window mat (staff, stores/multiWindow.ts): the ticked cart lines are the windows.
  const multiWindow = isMultiWindow(store);
  const mwPrints = multiWindow ? multiWindowPrints(store, $cart.get().lineItems as any[]) : [];
  if (opts.strict && multiWindow && mwPrints.length < 2)
    throw new Error(
      "Multi-window mat: tick at least 2 prints. For a single print, untick Multi-window and use the normal mat."
    );
  if (opts.strict && multiWindow && !pricing.applied)
    throw new Error("Multi-window mat: the price for this size isn't loaded yet — wait a moment and try again.");
  const price = pricing.applied
    ? pricing.value.toFixed(2)
    : store.currPricing?.replace?.("$", "");
  const fileName = selectedImage?.name || "";
  // Fine Art Print
  // HD Aluminum
  // Framed Print
  // Canvas
  const productType =
    store.selectedProduct === PRODUCT_TYPE.CANVAS
      ? "Canvas"
      : store.selectedProduct === PRODUCT_TYPE.HD_ALUMINUM
        ? "HD Aluminum"
        : store.selectedProduct === PRODUCT_TYPE.GICLEE &&
            !store.options.display
          ? "Fine Art Print"
          : store.selectedProduct === PRODUCT_TYPE.GICLEE &&
              store.options.display
            ? "Framed Print"
            : null;
  if (!productType) throw new Error("Failed to match the product type");
  const sizes = store.selectedSize;
  const paperType = store.options?.paperOptions?.type;
  const paperGloss =
    store.options?.paperOptions?.finish === 1 ? "true" : "false";
  // Staff custom (special-order) moulding: the cart line carries the custom label in place of
  // the stand-in frame's; the stand-in is kept at customMoulding.standIn so Edit / "Same specs"
  // restore the frame that renders (customMoulding.ts). A line with no cost entered still
  // carries the label — the backend adds nothing for it (NOT PRICED, shown in the panel).
  const customMoulding = customMouldingApplies(store.options)
    ? customMouldingOf(store.options.frameOptions)
    : null;
  // Custom mat / backing (09-22): kept only where they apply (matted / mat-only; float) so a
  // stale checkbox never rides into a treatment that has no such part; standIn = the colour shown.
  const customMat = customMatOf(store.options);
  const customBacking = customBackingOf(store.options);
  const baseFrame: any = store.options.frameOptions || {};
  const anyCustom =
    customMoulding || customMat || customBacking ||
    baseFrame.customMoulding || baseFrame.matboard?.custom || baseFrame.backing?.custom;
  const options = !anyCustom ? store.options : {
    ...store.options,
    frameOptions: {
      ...baseFrame,
      ...(customMoulding
        ? {
            label: customMouldingLabel(customMoulding),
            customMoulding: {
              ...customMoulding,
              standIn: String(baseFrame.label || ""),
            },
          }
        : { customMoulding: null }),
      matboard: {
        ...baseFrame.matboard,
        custom: customMat
          ? { ...customMat, standIn: matColorLabel({ ...baseFrame.matboard, custom: null }) }
          : null,
      },
      backing: {
        ...baseFrame.backing,
        custom: customBacking
          ? { ...customBacking, standIn: backingColorLabel(baseFrame.backing, true) }
          : null,
      },
    },
  };
  // Multi-window: the mat border is 0 so the size IS the glass size everywhere below (materials, overall size).
  const framing = multiWindowOptions(options, multiWindow);
  // Staff mount add-on: print mounted to Sintra / Dibond as the backing (framed lines only).
  const mountType = store.options?.display
    ? frameMountType(store.options?.frameOptions)
    : "";
  // Materials lines put the size first ("30x40 ..."): '30x40 1/8" Sintra' reads as 40 1/8, so name it after.
  // The sheet is adhesive coated — the work order says so (Michael 09-21).
  // Work-order wording, parallel to the foam lines (Michael 09-21): SF / Shadow / Matted backing
  // "Adhesive Coated DIBOND" (cf. "Adhesive Foam Board"); float "Matboard on DIBOND" (cf. "on Adhesive").
  const mountCode = !mountType ? "" : mountType.includes("Sintra") ? "SINTRA" : "DIBOND";
  const mountMaterial = mountCode ? `Adhesive Coated ${mountCode}` : "";
  const canvas = options;
  const canvasPaperOptions = options.paperOptions;
  const canvasPaper = options.paperOptions?.type;
  const canvasFraming = options;
  const selectedAluminum = options.finish || "";
  const selectedAlumFraming = options;

  // The mat bullet, joined onto the treatment line at the end ("Matted — 2\" White 4-ply Mat").
  let matLine = "";
  itemJson.cartData = {
    conditionalFields: [],
    materialsField1: null,
    materialsField2: null,
    materialsField3: null,
    materialsField4: null,
    materialsField5: null,
    materialsField6: null,
    materialsField7: null,
    materialsField8: null,
    materialsField9: null,
    materialsField10: null,
    noMats: false,
    staticField1: null,
    staticField2: `${sizes.dimensions.width}x${sizes.dimensions.height}`,
    staticField3: null,
    staticField4: fileName,
  };

  if (productType.match(/(fine art)/giu)) {
    itemJson.treatmentOptions = framing.treatmentOptions;
    // PHANTOM-FRAME FIX (2026-07-22): fine-art prints are UNFRAMED by
    // definition, but the visualizer store always carries the UI's default
    // frame (e.g. "Black Gallery", 0.75" face). Copying frameOptions
    // wholesale stamped that phantom frame onto every print and mat-only
    // item — shipping then classified mat-onlys as framed (one box each
    // instead of stacking). Keep ONLY the matboard data (genuinely used
    // for mat items below); zero the frame identity.
    itemJson.frameOptions = {
      dimensions: { depth: 0, face: 0, reveal: 0 },
      matboard: framing.frameOptions?.matboard,
      reveal: 0,
    };
    itemJson.paperOptions = framing.paperOptions;
    itemJson.sizing = sizes;
    itemJson.paperOptions = {
      ...framing.paperOptions,
      gloss: paperGloss === "true",
      type: paperType,
    };

    itemJson.cartData.staticField1 = "Fine Art Print";
    itemJson.cartData.staticField4 = framing?.treatmentOptions?.type;

    let paperOffset = (framing?.paperOptions?.size || 0) * 2;
    if (framing?.treatmentOptions?.type?.match(/(mat)/giu)) {
      paperOffset = (framing?.frameOptions?.matboard?.size || 0) * 2;
      itemJson.cartData.staticField4 = `No Frame, ${itemJson.cartData.staticField4}`;
    }

    // Staff mount-only (mountOnly.ts): "Mounted (on substrate)" — the sheet is the finished print (image + full
    // border); a floater frame adds reveal + 1/2" face per side to the overall (10-13: reveal is chosen, 1/8–1/2";
    // was a fixed 1/4" → 3/4" per side). The overall drives the shipping box.
    const mountOnly = mountOnlyOf(framing);
    const mountFloater = mountOnly ? mountFloaterLabel(mountOnly.frame) : "";
    const mountReveal = mountFloater ? Number(mountOnly?.reveal) : 0;
    const floaterOffset = mountFloater ? 2 * (mountReveal + MOUNT_FLOATER_FACE) : 0;

    const widthOd = sizes.dimensions.width + paperOffset + floaterOffset;
    const heightOd = sizes.dimensions.height + paperOffset + floaterOffset;
    itemJson.overallSizing = { width: widthOd, height: heightOd };

    itemJson.cartData.staticField2 = `${itemJson.cartData.staticField2} Image Size (Overall Dimensions ${parseFloat(
      widthOd.toFixed(3)
    )}x${parseFloat(heightOd.toFixed(3))})`;

    if (framing?.treatmentOptions?.type?.match(/mat/giu)) {
      itemJson.cartData.staticField1 += " - Matted Print";
    } else if (mountOnly) {
      // Title "Fine Art Print - Mount-Only" (Michael 09-30) — the substrate is the "Mounted on Dibond" bullet below.
      itemJson.cartData.staticField1 += " - Mount-Only";
      // No treatment bullet: "Mounted on Dibond" below says it (a bare "Mounted" line read as redundant, Michael 09-30).
      itemJson.cartData.staticField4 = null;
    } else {
      itemJson.cartData.staticField1 += " - Fine Art Giclee Print";
    }

    itemJson.cartData.staticField3 = itemJson.paperOptions.paper;

    if (!framing?.treatmentOptions?.type?.match(/mat/giu)) {
      let borderStr = "";
      if ((framing?.paperOptions?.size || 0) > 0) {
        borderStr += `${framing?.paperOptions?.size}" Border`;
      } else {
        borderStr += `Full Bleed`;
      }

      if (
        (framing?.paperOptions?.size || 0) > 0 &&
        framing?.paperOptions?.style === 1
      ) {
        borderStr += `; Straight-Cut Edge`;
      }

      if (
        framing?.paperOptions?.style === 2 ||
        framing?.paperOptions?.style === 3
      ) {
        borderStr += `; Hand Torn Deckled Edge`;
      }

      if (borderStr) itemJson.cartData.conditionalFields.push(borderStr);
    }

    if (mountOnly) {
      // Customer-facing spec (the quote tool's wording) + the Create-Item fields the Master List / work order /
      // cut list already read (`mntd on dbond in BLK FF w/ brace, cleat`) + the work-order tool's materials.
      const sub = mountOnly.substrate;
      const brace = mountOnly.brace; // mountOnlyOf already applies: floater ⇒ brace, never on foam
      itemJson.cartData.conditionalFields.push(`Mounted on ${mountSubstrateName(sub)}`);
      if (mountFloater) itemJson.cartData.conditionalFields.push(`${mountFloater}, ${mountRevealLabel(mountReveal)} reveal`);
      if (brace) itemJson.cartData.conditionalFields.push("Wood brace");
      if (mountOnly.cleat) itemJson.cartData.conditionalFields.push("French cleat");

      const sw = parseFloat((sizes.dimensions.width + paperOffset).toFixed(3));
      const sh = parseFloat((sizes.dimensions.height + paperOffset).toFixed(3));
      const dim = (w: number, h: number) => `${parseFloat(w.toFixed(3))}x${parseFloat(h.toFixed(3))}`;
      itemJson.cartData.materialsField1 = `${dim(sw, sh)} ${mountSubstrateMaterial(sub)}`;
      if (mountFloater || brace) {
        // Adhered platform (back brace): inset 1/2" per side inside a floater, 1" for a wall hang.
        const off = mountFloater ? 0.5 : 1;
        itemJson.cartData.materialsField2 = `${dim(sw - off * 2, sh - off * 2)} Adhered Platform (shop confirm)`;
      }
      // Floater frame size = print + reveal each side.
      if (mountFloater) itemJson.cartData.materialsField3 = `${dim(sw + 2 * mountReveal, sh + 2 * mountReveal)} ${mountFloater}`;
      if (mountOnly.cleat) itemJson.cartData.materialsField7 = "French Cleat";

      itemJson.mountSubstrate = sub;
      itemJson.brace = brace;
      itemJson.cleat = !!mountOnly.cleat;
      if (mountOnly.frame) {
        itemJson.frameOptions = { ...itemJson.frameOptions, label: mountOnly.frame };
        itemJson.usesFrame = true;
        itemJson.floaterReveal = mountReveal; // per side — cut list sizes the floater from it
      }
    }

    if (framing?.treatmentOptions?.type?.match(/mat/giu)) {
      // TODO - dont know how to get to here
      itemJson.cartData.conditionalFields.push(
        (matLine = matText(framing?.frameOptions?.matboard))
      );

      itemJson.cartData.materialsField4 = `${sizes.dimensions.width + paperOffset}x${
        sizes.dimensions.height +
        (framing?.frameOptions?.matboard?.size || 0) * 2
      } Mat Outer Dimensions`;
      itemJson.cartData.materialsField5 = `${sizes.dimensions.width}x${sizes.dimensions.height} Mat window`;
    }
  } else if (productType.match(/(framed)/giu)) {
    itemJson.treatmentOptions = framing.treatmentOptions;
    itemJson.frameOptions = framing.frameOptions;
    itemJson.paperOptions = framing.paperOptions;
    itemJson.sizing = sizes;
    itemJson.paperOptions = {
      ...framing.paperOptions,
      gloss: paperGloss === "true",
      type: paperType,
    };

    const revealOffset = (framing?.frameOptions?.reveal || 0) * 2;
    let paperOffset = (framing?.paperOptions?.size || 0) * 2;
    if (framing?.treatmentOptions?.type?.match(/(mat)/giu)) {
      paperOffset = (framing?.frameOptions?.matboard?.size || 0) * 2;
    }

    itemJson.cartData.staticField1 = "Framed Fine Art Print";
    itemJson.cartData.staticField4 = framing?.treatmentOptions?.type;

    let widthOd =
      sizes.dimensions.width +
      revealOffset +
      paperOffset +
      (framing?.frameOptions?.dimensions?.face || 0) * 2 -
      0.5;
    let heightOd =
      sizes.dimensions.height +
      revealOffset +
      paperOffset +
      (framing?.frameOptions?.dimensions?.face || 0) * 2 -
      0.5;

    if (framing?.treatmentOptions?.type?.match(/float/giu)) {
      widthOd += 0.5;
      heightOd += 0.5;
    }

    const matMaterial = matText(framing?.frameOptions?.matboard);

    itemJson.overallSizing = { width: widthOd, height: heightOd };
    itemJson.cartData.staticField2 = `${itemJson.cartData.staticField2} Image Size (Overall Dimensions ${parseFloat(
      widthOd.toFixed(3)
    )}x${parseFloat(heightOd.toFixed(3))})`;

    if (framing?.treatmentOptions?.type?.match(/straight/giu)) {
      itemJson.cartData.staticField1 += " - Straight Fit";
    } else if (framing?.treatmentOptions?.type?.match(/matted/giu)) {
      itemJson.cartData.staticField1 += " - Matted and Framed";
      itemJson.cartData.materialsField4 = `${sizes.dimensions.width + (framing?.frameOptions?.matboard?.size || 0) * 2}x${
        sizes.dimensions.height +
        (framing?.frameOptions?.matboard?.size || 0) * 2
      } Mat Outer Dimensions`;
      itemJson.cartData.materialsField5 = `${sizes.dimensions.width}x${sizes.dimensions.height} Mat Window`;
      itemJson.cartData.materialsField5_5 = matMaterial; // Named 5_5 because didnt want to increment all the numbers again. ie 5, 5.5, 6
      itemJson.cartData.materialsField6 = `${sizes.dimensions.width + (framing?.frameOptions?.matboard?.size || 0) * 2}x${
        sizes.dimensions.height +
        (framing?.frameOptions?.matboard?.size || 0) * 2
      } ${mountMaterial || "Acid-Free Backing"}`;
      // FIX 2026-08-14: was `/exhibition/`, which is DEAD — nothing has ever
      // stored the word "exhibition". Both the plain and the "Exhibition"
      // Shadow Box store treatmentOptions.type === "Shadow Box" with an empty
      // subcategory, so this chain fell through entirely and a Shadow Box got
      // NO treatment suffix at all (staticField1 stayed "Framed Fine Art
      // Print") on the confirmation, invoice, packing slip and work order.
      //
      // Per Michael 2026-08-14: "Exhibition Shadow Box" is NOT a distinct
      // treatment. It is the same Shadow Box construction; the checkbox is a
      // convenience that defaults the border slider to 2" for customers who
      // knew the old static-site product name. Same build, same price. So one
      // label is correct here and there is nothing to disambiguate.
      // (Do not confuse the Shadow Box TREATMENT with a shadow-box MOULDING —
      // see TPW_FRAMING_VOCABULARY.md.)
    } else if (framing?.treatmentOptions?.type?.match(/shadow/giu)) {
      itemJson.cartData.staticField1 += " - Shadow Box";
    } else if (framing?.treatmentOptions?.type?.match(/float/giu)) {
      if (framing?.treatmentOptions?.subcategory?.match(/modern/giu)) {
        itemJson.cartData.staticField1 += " - Modern Float";
      } else {
        itemJson.cartData.staticField1 += " - Museum Float";
      }
    }

    itemJson.cartData.staticField3 = itemJson.paperOptions.paper;

    if (!framing?.treatmentOptions?.type?.match(/matted/giu)) {
      let borderStr = "";

      if ((framing?.paperOptions?.size || 0) > 0) {
        borderStr += `${framing?.paperOptions?.size}" Border`;
      } else {
        borderStr += `Full Bleed`;
      }

      if (
        !framing?.treatmentOptions?.type?.match(/straight/giu) &&
        !framing?.treatmentOptions?.type?.match(/exhibition/giu)
      ) {
        if (framing?.paperOptions?.style === 1) {
          borderStr += `; Straight-Cut Edge`;
        }
      }

      if (
        framing?.paperOptions?.style === 2 ||
        framing?.paperOptions?.style === 3
      ) {
        borderStr += `; Hand Torn Deckled Edge`;
      }

      if (borderStr) itemJson.cartData.conditionalFields.push(borderStr);

      // Float mounts: backing color rides on the Reveal spec bullet so the
      // confirmation / packing slip show it without a full materials list
      // (per Michael 2026-08-30), e.g. `1" Reveal, Antique White Backing`.
      const isFloat = !!framing?.treatmentOptions?.type?.match(/float/giu);
      const floatBacking = isFloat
        ? backingColorLabel(framing?.frameOptions?.backing)
        : "";
      if ((framing?.frameOptions?.reveal || 0) > 0) {
        itemJson.cartData.conditionalFields.push(
          `${framing?.frameOptions?.reveal}" Reveal${floatBacking ? `, ${floatBacking} Backing` : ""}`
        );
      }
    }

    if (framing?.treatmentOptions?.type?.match(/mat/giu)) {
      itemJson.cartData.conditionalFields.push((matLine = matMaterial));
    }

    let frameLabel = framing?.frameOptions?.label;
    if (!framing?.treatmentOptions?.type?.match(/(no|only)/giu)) {
      if (!frameLabel?.match(/frame/giu) && !isCustomMouldingLabel(frameLabel)) {
        frameLabel += " Frame";
      }

      itemJson.cartData.conditionalFields.push(frameLabel);
      itemJson.cartData.conditionalFields.push(
        framing?.frameOptions?.glass?.type
      );
      if (mountType) {
        itemJson.cartData.conditionalFields.push(`Mounted to ${mountType}`);
      }
    }

    if (framing?.treatmentOptions?.type?.match(/straight/giu)) {
      itemJson.cartData.materialsField1 = `${sizes.dimensions.width + paperOffset}x${sizes.dimensions.height + paperOffset} ${frameMaterial(frameLabel)}`;
      itemJson.cartData.materialsField7 = `${sizes.dimensions.width + paperOffset}x${
        sizes.dimensions.height + paperOffset
      } ${mountMaterial || "Adhesive Foam Board"}`;

      itemJson.cartData.materialsField10 = `${sizes.dimensions.width + paperOffset}x${sizes.dimensions.height + paperOffset} ${
        framing?.frameOptions?.glass?.type
      }`;
    } else if (framing?.treatmentOptions?.type?.match(/matted/giu)) {
      itemJson.cartData.materialsField1 = `${sizes.dimensions.width + (framing?.frameOptions?.matboard?.size || 0) * 2}x${
        sizes.dimensions.height +
        (framing?.frameOptions?.matboard?.size || 0) * 2
      } ${frameMaterial(frameLabel)}`;

      itemJson.cartData.materialsField10 = `${sizes.dimensions.width + (framing?.frameOptions?.matboard?.size || 0) * 2}x${
        sizes.dimensions.height +
        (framing?.frameOptions?.matboard?.size || 0) * 2
      } ${framing?.frameOptions?.glass?.type}`;
    } else if (framing?.treatmentOptions?.type?.match(/shadow/giu)) {
      itemJson.cartData.materialsField1 = `${sizes.dimensions.width + paperOffset}x${sizes.dimensions.height + paperOffset} ${frameMaterial(frameLabel)}`;
      itemJson.cartData.materialsField7 = `${sizes.dimensions.width + paperOffset}x${
        sizes.dimensions.height + paperOffset
      } ${mountMaterial || "Adhesive Foam Board"}`;
      itemJson.cartData.materialsField10 = `${sizes.dimensions.width + paperOffset}x${sizes.dimensions.height + paperOffset} ${
        framing?.frameOptions?.glass?.type
      }`;
    } else if (framing?.treatmentOptions?.type?.match(/float/giu)) {
      itemJson.cartData.materialsField1 = `${sizes.dimensions.width + paperOffset + revealOffset + 0.5}x${
        sizes.dimensions.height + paperOffset + revealOffset + 0.5
      } ${frameMaterial(frameLabel)}`;
      itemJson.cartData.materialsField8 = `${sizes.dimensions.width + paperOffset + revealOffset + 0.5}x${
        sizes.dimensions.height + paperOffset + revealOffset + 0.5
      } ${withColor(backingColorLabel(framing?.frameOptions?.backing))}Matboard on ${mountCode || "Adhesive"}`;
      // 2026-09-06: DSA = print size - 0.5 (shop standard; was - 1).
      // 2026-09-18: prints of 36x48 incl. border and up (12 sq ft) mount on a
      // sintra platform at print size instead — same rule as the cut list
      // (tpw-server-functions cutList.ts sintraMount).
      const printW = sizes.dimensions.width + paperOffset;
      const printH = sizes.dimensions.height + paperOffset;
      itemJson.cartData.materialsField9 =
        printW * printH >= 36 * 48 - 1e-6
          ? `${printW}x${printH} Sintra Platform`
          : `${printW - 0.5}x${printH - 0.5} Double-Sided Adhesive`;
      // Spacers always match the backing color on float mounts. Dimensions
      // intentionally omitted until production confirms the sizing rule --
      // do not guess them. Line is skipped entirely when the color is unknown.
      const spacerColor = backingColorLabel(framing?.frameOptions?.backing, true);
      if (spacerColor) {
        itemJson.cartData.materialsField7 = `${spacerColor} Spacers`;
      }
      itemJson.cartData.materialsField10 = `${sizes.dimensions.width + paperOffset + revealOffset + 0.5}x${
        sizes.dimensions.height + paperOffset + revealOffset + 0.5
      } ${framing?.frameOptions?.glass?.type}`;
    }
  } else if (productType.match(/(canvas)/giu)) {
    itemJson.frameOptions = canvasFraming.frameOptions;
    itemJson.treatmentOptions = canvasFraming.treatmentOptions;
    itemJson.sizing = sizes;
    itemJson.type = canvas?.treatmentOptions?.type;
    itemJson.paperOptions = { ...canvasPaperOptions, type: canvasPaper };

    itemJson.cartData.staticField1 = "Canvas Giclee";
    itemJson.cartData.staticField4 = canvasFraming?.treatmentOptions?.type;

    const looseWidth = 2 * 3.5;
    const frameWidth = 2 * 0.75;
    let widthOd = sizes.dimensions.width;
    let heightOd = sizes.dimensions.height;

    let frameLabel = itemJson.frameOptions.label;
    if (!frameLabel.match(/frame/giu) && !isCustomMouldingLabel(frameLabel)) {
      frameLabel += " Frame";
    }

    if (canvas?.treatmentOptions?.type?.match(/stretcher/giu)) {
      itemJson.overallSizing = { width: widthOd, height: heightOd };
      itemJson.cartData.staticField2 = `${itemJson.cartData.staticField2} Image Size (Overall Dimensions ${parseFloat(
        widthOd.toFixed(3)
      )}x${parseFloat(heightOd.toFixed(3))})`;
    } else if (canvas?.treatmentOptions?.type?.match(/loose/giu)) {
      // Blank border per side from the panel (staff, 09-22); the old fixed 3.5" stands in when none is stored.
      const lbSide = Number(canvas?.paperOptions?.size) || 0;
      const lb = lbSide > 0 ? lbSide * 2 : looseWidth;
      widthOd += lb;
      heightOd += lb;
      itemJson.overallSizing = { width: widthOd, height: heightOd };
      itemJson.cartData.staticField2 = `${itemJson.cartData.staticField2} Image Size (Overall Dimensions ${parseFloat(
        widthOd.toFixed(3)
      )}x${parseFloat(heightOd.toFixed(3))})`;
    } else if (canvas?.treatmentOptions?.type?.match(/frame/giu)) {
      widthOd += frameWidth;
      heightOd += frameWidth;
      itemJson.overallSizing = { width: widthOd, height: heightOd };
      itemJson.cartData.staticField2 = `${itemJson.cartData.staticField2} Image Size (Overall Dimensions ${parseFloat(
        widthOd.toFixed(3)
      )}x${parseFloat(heightOd.toFixed(3))})`;
    }

    if (canvas?.treatmentOptions?.type?.match(/stretcher/giu)) {
      itemJson.cartData.staticField1 += " - Stretched Canvas";
      itemJson.cartData.materialsField2 = `${sizes.dimensions.width}x${sizes.dimensions.height} Stretcher`;
    } else if (canvas?.treatmentOptions?.type?.match(/loose/giu)) {
      itemJson.cartData.staticField1 += " - Loose Canvas Print";
      const lb = Number(canvas?.paperOptions?.size) || 3.5;
      itemJson.cartData.conditionalFields.push(`${lb}" Blank Border`);
      itemJson.cartData.conditionalFields.push("Unstretched, rolled");
    } else if (canvas?.treatmentOptions?.type?.match(/float/giu)) {
      itemJson.cartData.staticField1 += " - Canvas in Floater Frame";
      itemJson.cartData.materialsField2 = `${sizes.dimensions.width}x${sizes.dimensions.height} Stretcher`;
      itemJson.cartData.materialsField3 = `${sizes.dimensions.width + 0.5}x${sizes.dimensions.height + 0.5} ${frameMaterial(frameLabel)}`;
    }

    itemJson.cartData.staticField3 = canvasPaper;

    if (canvas?.paperOptions?.mirrored) {
      itemJson.cartData.conditionalFields.push(`Mirrored Edge`);
    } else if (canvas?.paperOptions?.border) {
      if (canvas?.paperOptions?.borderColor === "#ffffff") {
        itemJson.cartData.conditionalFields.push(`Border Color: White`);
      } else if (canvas.paperOptions?.borderColor === "#000000") {
        itemJson.cartData.conditionalFields.push(`Border Color: Black`);
      } else {
        itemJson.cartData.conditionalFields.push(
          `Border Color: ${canvas.paperOptions?.borderColor}`
        );
      }
    }

    if (canvas?.treatmentOptions?.type?.match(/frame/giu)) {
      itemJson.cartData.conditionalFields.push(frameLabel);
    }
  } else if (productType.match(/(aluminum)/giu)) {
    itemJson.frameOptions = selectedAlumFraming.frameOptions;
    itemJson.treatmentOptions = selectedAlumFraming.treatmentOptions;
    itemJson.sizing = sizes;
    itemJson.type = selectedAluminum;

    if (selectedAlumFraming?.treatmentOptions?.type?.match(/float/giu)) {
      itemJson.cartData.staticField1 = "HD Aluminum in Floater Frame";
    } else {
      itemJson.cartData.staticField1 = "HD Aluminum";
    }
    itemJson.cartData.staticField4 =
      selectedAlumFraming?.treatmentOptions?.type;

    const frameWidth = 2 * 0.75;
    let widthOd = sizes.dimensions.width;
    let heightOd = sizes.dimensions.height;

    let frameLabel = itemJson.frameOptions.label;
    if (!frameLabel.match(/frame/giu) && !isCustomMouldingLabel(frameLabel)) {
      frameLabel += " Frame";
    }

    if (
      selectedAlumFraming?.treatmentOptions?.type?.match(/panel|regular/giu)
    ) {
      itemJson.overallSizing = { width: widthOd, height: heightOd };
      itemJson.cartData.staticField2 = `${itemJson.cartData.staticField2} Image Size (Overall Dimensions ${parseFloat(
        widthOd.toFixed(3)
      )}x${parseFloat(heightOd.toFixed(3))})`;
    } else if (
      selectedAlumFraming?.treatmentOptions?.type?.match(/floater/giu)
    ) {
      widthOd += frameWidth;
      heightOd += frameWidth;
      itemJson.overallSizing = { width: widthOd, height: heightOd };
      itemJson.cartData.staticField2 = `${itemJson.cartData.staticField2} Image Size (Overall Dimensions ${parseFloat(
        widthOd.toFixed(3)
      )}x${parseFloat(heightOd.toFixed(3))})`;
    }

    if (selectedAlumFraming?.treatmentOptions?.type?.match(/float/giu)) {
      itemJson.cartData.materialsField2 = `${sizes.dimensions.width - 0.5}x${sizes.dimensions.height - 0.5} VHB Platform`;
      itemJson.cartData.materialsField3 = `${sizes.dimensions.width + 0.5}x${sizes.dimensions.height + 0.5} ${frameMaterial(frameLabel)}`;
    }

    itemJson.cartData.staticField3 = selectedAluminum;

    if (selectedAlumFraming?.treatmentOptions?.type?.match(/floater/giu)) {
      itemJson.cartData.conditionalFields.push(frameLabel);
    }
  }

  // The separate "White Mat" bullet is gone (Michael 09-24): the mat line above already names the colour
  // ('2" White 4-ply Mat'), so it only repeated it — on every matted line, cart / confirmation / work order.


  if (pricing.requested && !pricing.multi) {
    const order = String(store.prevPrintedOrder || "").trim();
    itemJson.prevPrinted = true;
    itemJson.prevPrintedOrder = order;
    itemJson.fullPrice = store.currPricing?.replace?.("$", "");
    itemJson.printPriceWaived = pricing.waived;
    // conditionalFields surface on the cart line, checkout, confirmation,
    // work order and packing slip — one push covers every document.
    itemJson.cartData.conditionalFields.push(prevPrintedNote(order));
  }

  if (multiWindow && pricing.multi && mwPrints.length) {
    // Multi-window mat: this line is the mat / mat + frame; its prints are their own lines (listed under Windows).
    // Wording (Michael 09-24): the treatment and the mat on ONE line ("Matted — 3-window White 8-ply Mat"), no
    // paper line, and ONE materials line for the mat + its windows.
    const n = mwPrints.length;
    const sizesTxt = multiWindowSizesText(mwPrints);
    const mb = framing?.frameOptions?.matboard;
    const zeroMat = matText(mb);
    const mwMat = matText(mb, n);
    const title = `Multi-window mat - ${n} windows`;
    itemJson.cartData.conditionalFields = itemJson.cartData.conditionalFields.map((f: string) =>
      f === zeroMat ? mwMat : f
    );
    matLine = mwMat;
    itemJson.cartData.conditionalFields.push(
      // One dash per window on its own line (long file names read apart, Michael 09-24). Where the newlines
      // aren't honoured (emails) the dashes still separate them inline.
      `Windows:\n${mwPrints.map(p => `– ${p.fileName} (${parseFloat(p.width.toFixed(3))}x${parseFloat(p.height.toFixed(3))})`).join("\n")}`
    );
    itemJson.cartData.staticField2 = String(itemJson.cartData.staticField2 || "").replace("Image Size", "Glass Size");
    itemJson.cartData.staticField3 = null;
    if (itemJson.cartData.materialsField5)
      itemJson.cartData.materialsField5 = `${mwMat} (lay out by hand): ${sizesTxt}`;
    if (itemJson.cartData.materialsField5_5 === zeroMat) itemJson.cartData.materialsField5_5 = null;
    itemJson.fileName = title;
    itemJson.cartData.fileName = title;
    itemJson.multiWindow = { windows: n, extra: pricing.multi.extra, prints: mwPrints };
    itemJson.fullPrice = store.currPricing?.replace?.("$", "");
    itemJson.printPriceWaived = pricing.waived;
  }


  if (store.suppliedArt && isSuppliedArtEligible(store) && !pricing.requested) {
    // Customer supplied art: full price stands; the paper name is replaced by
    // the supplied label (staticField3 feeds every document), and `supplied`
    // drives the masterlist "suppl'd prnt" vocabulary + work-order warning.
    itemJson.supplied = true;
    // The staffer's name for the piece stands in for the placeholder's file name everywhere a file name
    // prints (cart line, confirmation, work order, Master List, cut list), and the paper line reads
    // "Customer supplied art - red square" (Michael 09-22).
    const title = String(store.suppliedTitle || "").trim();
    itemJson.cartData.staticField3 = suppliedArtLabel(title);
    if (title) {
      itemJson.fileName = title;
      itemJson.cartData.fileName = title;
      if (itemJson.cartData.staticField4 === fileName) itemJson.cartData.staticField4 = title;
    }
  }

  // Treatment + mat on one line (Michael 09-24): "Matted — 2\" White 4-ply Mat", not two bullets.
  if (matLine && itemJson.cartData.conditionalFields.includes(matLine)) {
    itemJson.cartData.conditionalFields = itemJson.cartData.conditionalFields.filter((f: string) => f !== matLine);
    const t = String(itemJson.cartData.staticField4 || "").trim();
    itemJson.cartData.staticField4 = t && t !== fileName ? `${t} — ${matLine}` : matLine;
  }

  itemJson.sizing = sizes;
  itemJson.price = price;
  itemJson.productType = productType;
  if (!itemJson.fileName) itemJson.fileName = fileName;

  if (selectedImage) {
    itemJson.realWidth = selectedImage.realWidth;
    itemJson.realHeight = selectedImage.realHeight;
  }

  if (
    !itemJson.cartData.materialsField1 &&
    !itemJson.cartData.materialsField2 &&
    !itemJson.cartData.materialsField3 &&
    !itemJson.cartData.materialsField4 &&
    !itemJson.cartData.materialsField5 &&
    !itemJson.cartData.materialsField5_5 &&
    !itemJson.cartData.materialsField6 &&
    !itemJson.cartData.materialsField7 &&
    !itemJson.cartData.materialsField8 &&
    !itemJson.cartData.materialsField9
  ) {
    itemJson.cartData.noMats = true;
  }

  return itemJson;
};
